import type { FC } from "react";
import { useCallback, useState } from "react";

import { useQueryClient } from "@tanstack/react-query";
import { useDispatch } from "react-redux";

import { translate } from "app/filters/i18nFilter";
import {
    renameMenuItem,
    deleteMenuItem,
    getModifyMenuItem,
} from "app/pages/sales-intelligence/common-components/options-menu/items";
import type { MenuItemOption } from "app/pages/sales-intelligence/common-components/options-menu/types";
import useSavedSearchTrackingService from "app/pages/sales-intelligence/hooks/useSavedSearchTrackingService";
import useTargetAccountsTrackingService from "app/pages/sales-intelligence/hooks/useTargetAccountsTrackingService";
import SavedSearchDeleteModalContainer from "app/pages/sales-intelligence/modules/leads-search/sub-modules/saved-search/containers/SavedSearchDeleteModal/SavedSearchDeleteModalContainer";
import type { SavedSearchesDto } from "app/pages/sales-intelligence/modules/leads-search/sub-modules/saved-search/types/dto";
import ListItem from "app/pages/sales-intelligence/modules/lists/components/ListItem/ListItem";
import NewWebsitesButton from "app/pages/sales-intelligence/modules/lists/components/NewWebsitesButton/NewWebsitesButton";
import SavedSearchesRenameModal from "app/pages/sales-intelligence/modules/lists/components/SavedSearchesRenameModal/SavedSearchesRenameModal";
import { StyledOptionsMenu } from "app/pages/sales-intelligence/modules/lists/components/styles";
import { PAGE_SIZE } from "app/pages/sales-intelligence/modules/lists/constants/constants";
import {
    StyledCustomIcon,
    StyledListHistoryPagination,
} from "app/pages/sales-intelligence/modules/lists/containers/list/StaticListContainer/styles";
import { listsQueryKeys } from "app/pages/sales-intelligence/modules/lists/sub-modules/my-list/services/queries/queryKeyFactory";
import siNavigator from "app/pages/sales-intelligence/services/siNavigator";
import { deleteSavedSearchModalToggledAction } from "app/pages/sales-intelligence/sub-modules/advanced-search/store/action-creators";
import Pagination from "app/pages/sales-intelligence/sub-modules/contacts-search/components/Pagination/Pagination";
import type { ThunkDispatchCommon } from "single-spa/store/types";

import { StyledListContainer, StyledListItemsContainer } from "./styles";

type SavedSearchesContainerProps = {
    records: SavedSearchesDto[];
    totalCount: number;
    setPage: (page: number) => void;
    refetch: () => void;
    page: number;
};

const SavedSearchesContainer: FC<SavedSearchesContainerProps> = ({
    records,
    totalCount,
    setPage,
    page: pageData,
    refetch,
}) => {
    const [listBeingRenamed, setListBeingRenamed] = useState<SavedSearchesDto | undefined>(undefined);
    const trackingService = useTargetAccountsTrackingService();
    const savedSearchTrackingService = useSavedSearchTrackingService();

    const queryClient = useQueryClient();

    const dispatch: ThunkDispatchCommon = useDispatch();

    const closeRenameModal = () => {
        setListBeingRenamed(undefined);
        void refetch();
    };

    const optionMenu = [
        getModifyMenuItem(renameMenuItem, { text: { key: "si.options-menu.saved-search.rename" } }),
        getModifyMenuItem(deleteMenuItem, { text: { key: "si.options-menu.saved-search.delete" } }),
    ];

    const onChangePageHandler = useCallback(
        async (page: number) => {
            if (page !== pageData) {
                await setPage(page);
            }
        },
        [setPage, pageData],
    );

    const handleToCurrentSavedSearch = (id: string) => {
        trackingService.trackMyListSavedSearchClicked();
        siNavigator.goToSavedSearchPageById({
            savedSearchId: id,
        });
    };

    const handleNewWebsitesClick = (id: string) => {
        siNavigator.goToSavedSearchPageById({
            savedSearchId: id,
            isNewOnly: true,
        });
    };

    const handleMenuItemSelect = (option: MenuItemOption, list: Readonly<SavedSearchesDto>) => {
        const { name, totalCount, id, newCount } = list;
        switch (option) {
            case "rename":
                return setListBeingRenamed(list);
            case "delete":
                return dispatch(
                    deleteSavedSearchModalToggledAction({
                        name,
                        totalNumberOfWebsites: totalCount,
                        searchId: id,
                        numberOfNewWebsites: newCount,
                    }),
                );
            default:
                return;
        }
    };
    const handleTrackedOptionsMenuToggle = (isOpen: boolean): void => {
        savedSearchTrackingService.trackSavedSearchOptionsToggled(isOpen);
    };

    const handleDeleteSavedSearch = () => {
        if (records.length === 1 && pageData > 1) {
            setPage(pageData - 1);
        }

        void queryClient.invalidateQueries({ queryKey: listsQueryKeys.allSavedSearches() });
    };

    return (
        <StyledListContainer>
            <SavedSearchDeleteModalContainer onDelete={handleDeleteSavedSearch} />
            <SavedSearchesRenameModal
                onClose={closeRenameModal}
                list={listBeingRenamed}
            />

            <StyledListItemsContainer>
                {records.map((item: SavedSearchesDto) => {
                    const { id, name, totalCount, lastUpdated, newCount } = item;

                    return (
                        <ListItem
                            key={id}
                            name={name}
                            icon={<StyledCustomIcon iconName="search-radar" />}
                            lastUpdated={lastUpdated ? new Date(lastUpdated) : undefined}
                            websitesCount={totalCount}
                            onClick={() => handleToCurrentSavedSearch(id)}
                            metadataElements={[
                                <NewWebsitesButton
                                    key={`new-results-button-${id}`}
                                    count={newCount}
                                    title={translate("si.my-lists.saved-searches.title.new-results")}
                                    tooltipContent={translate("si.my-lists.saved-searches.tooltip.new-results")}
                                    onClick={() => handleNewWebsitesClick(id)}
                                />,
                                <StyledOptionsMenu
                                    id={`static-list-item-${id}`}
                                    key={`static-list-item-${id}`}
                                    items={optionMenu}
                                    onSelect={(option) => handleMenuItemSelect(option, item)}
                                    tooltipContent={translate("si.common.option-menu.placeholder")}
                                    onToggle={handleTrackedOptionsMenuToggle}
                                    isAppendToBody={false}
                                />,
                            ]}
                        />
                    );
                })}
            </StyledListItemsContainer>
            {totalCount > PAGE_SIZE ? (
                <StyledListHistoryPagination>
                    <Pagination
                        page={pageData}
                        pageSize={PAGE_SIZE}
                        totalCount={totalCount}
                        onPageChange={onChangePageHandler}
                    />
                </StyledListHistoryPagination>
            ) : null}
        </StyledListContainer>
    );
};

export default SavedSearchesContainer;
